import styled from '@emotion/styled';
import Link from 'next/link';
import { FC } from 'react';

import { ArticleMarkdown } from '@/components/ui/shared/markdowns/ArticleMarkdown/ArticleMarkdown';
import { useAnalytics } from '@/context/Analytics';
import { FontFamily } from '@/enums/index';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useResource } from '@/lib/datx-jsonapi-react';
import { getNewsArticleById } from '@/queries/news-articles';
import { BREAKPOINTS } from '@/styles/themes/default';
import { IThemeProps } from '@/styles/themes/types';
import { dateToString } from '@/utils/date';
import { splitIdAndSlug } from '@/utils/helperFunctions';

const ContentWrapper = styled.div<IThemeProps>`
  padding: 0 20px;
  font-size: 16px;
  line-height: 24px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    max-width: 512px;
    margin-left: auto;
    margin-right: auto;
    font-size: 20px;
    line-height: 32px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    max-width: 692px;
  }
`;

const Author = styled.div<IThemeProps>`
  color: ${({ theme }) => theme.legacy.colors.lightText};
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.SpaceGrotesk]};
  margin: 40px 0 56px;
`;

const AuthorName = styled.span<IThemeProps>`
  color: ${({ theme }) => theme.legacy.colors.brand.primary};
  font-family: ${FontFamily.SpaceGrotesk};
  font-weight: bold;
  margin-right: 16px;
  font-size: 16px;
  line-height: 24px;
`;

const RelatedTopics = styled.div<IThemeProps>`
  display: flex;
  flex-direction: column;
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.SpaceGrotesk]};
  padding-bottom: 50px;
  border-bottom: 1px solid #ccc;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    flex-direction: row;
    overflow: auto;
    ${({ theme }) => theme.legacy.scrollbarXStyle()};
  }

  span {
    white-space: nowrap;
  }
`;

const TopicLink = styled.a<IThemeProps>`
  color: ${({ theme }) => theme.legacy.colors.brand.primary};
  font-family: ${FontFamily.SpaceGrotesk};
  font-size: 20px;
  line-height: 32px;
  font-weight: bold;
  text-decoration: none;
  white-space: nowrap;
  margin: 10px 0;

  :first-of-type {
    margin-top: 24px;
  }

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    margin: 0 12px;

    :first-of-type {
      margin-top: 0;
      margin-left: 32px;
    }
  }

  &:hover {
    font-family: ${FontFamily.SpaceGroteskUnderline};
  }
`;

export const Content: FC = () => {
  const analytics = useAnalytics();
  const handleTopicClick = (topic: string) => {
    return () => {
      analytics.track('article_topic_select', { article_topic: topic });
    };
  };

  const { id: articleSlug } = useQueryParams<{ id: string }>({
    id: StringParam,
  });

  const { id: articleId } = splitIdAndSlug(articleSlug);

  const { data: article } = useResource(getNewsArticleById(articleId));

  return (
    <ContentWrapper>
      <ArticleMarkdown source={article.content} escapeHtml={false} />
      <Author>
        <AuthorName>{article.author}</AuthorName>—&nbsp;
        {dateToString(article.publishedOn, 'MMMM do, yyyy')}
      </Author>
      <RelatedTopics>
        <span>Read more about</span>
        {article.newsArticleTopics.map((topic) => (
          <Link
            key={topic.id}
            href={{ pathname: '/news', query: { articleTopicId: topic.id } }}
            passHref
            legacyBehavior
          >
            <TopicLink onClick={handleTopicClick(topic.name)}>{topic.name}</TopicLink>
          </Link>
        ))}
      </RelatedTopics>
    </ContentWrapper>
  );
};
